/** Accessible image-only chart zoom, including charts replaced after a variant change. */
class BrSizeChartViewer extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.scale = 1;
    this.pointers = new Map();
    const options = { signal: this.controller.signal };
    this.dialog = this.closest('dialog');
    this.dialog?.addEventListener('click', (event) => {
      const action = event.target.closest('[data-chart-zoom]')?.dataset.chartZoom;
      if (action) this.zoom(action === 'reset' ? 1 : this.scale + (action === 'in' ? 0.5 : -0.5));
    }, options);
    this.addEventListener('load', (event) => {
      if (event.target instanceof HTMLImageElement) this.fit();
    }, { ...options, capture: true });
    this.addEventListener('keydown', (event) => {
      if (!['+', '=', '-', '0'].includes(event.key)) return;
      event.preventDefault();
      this.zoom(event.key === '0' ? 1 : this.scale + (event.key === '-' ? -0.5 : 0.5));
    }, options);
    this.addEventListener('dblclick', (event) => {
      if (event.target.closest('.br-size-chart__media')) this.zoom(this.scale > 1 ? 1 : 2);
    }, options);
    this.addEventListener('wheel', (event) => {
      if (!event.ctrlKey || !event.target.closest('.br-size-chart__media')) return;
      event.preventDefault();
      this.zoom(this.scale + (event.deltaY < 0 ? 0.25 : -0.25));
    }, { ...options, passive: false });
    this.addEventListener('pointerdown', (event) => {
      if (!event.target.closest('.br-size-chart__media') || event.button !== 0) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.media?.setPointerCapture(event.pointerId);
      if (this.pointers.size === 2) {
        this.pinchDistance = this.distance();
        this.pinchScale = this.scale;
      }
    }, options);
    this.addEventListener('pointermove', (event) => {
      const previous = this.pointers.get(event.pointerId);
      if (!previous || !this.media) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size === 2 && this.pinchDistance > 0) {
        const points = [...this.pointers.values()];
        const rect = this.media.getBoundingClientRect();
        this.zoom(this.pinchScale * this.distance() / this.pinchDistance, {
          x: (points[0].x + points[1].x) / 2 - rect.left,
          y: (points[0].y + points[1].y) / 2 - rect.top,
        });
      } else if (this.scale > 1) {
        this.media.scrollLeft += previous.x - event.clientX;
        this.media.scrollTop += previous.y - event.clientY;
      }
    }, options);
    const release = (event) => { this.pointers.delete(event.pointerId); this.pinchDistance = 0; };
    this.addEventListener('pointerup', release, options);
    this.addEventListener('pointercancel', release, options);
    this.openObserver = new MutationObserver(() => {
      this.pointers.clear();
      if (this.dialog?.open) requestAnimationFrame(() => this.fit());
    });
    if (this.dialog) this.openObserver.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
    this.imageObserver = new MutationObserver(() => this.fit());
    const content = this.matches('.br-size-chart__content') ? this : this.querySelector('.br-size-chart__content');
    if (content) this.imageObserver.observe(content, { subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'srcset'] });
    this.resizeObserver = new ResizeObserver(() => {
      // A zoomed image creates scrollbars, which change clientWidth. Only a
      // change to the outer viewport should reset the zoom level.
      const width = this.getBoundingClientRect().width;
      if (width !== this.lastWidth) { this.lastWidth = width; this.fit(); }
    });
    this.resizeObserver.observe(this, { box: 'border-box' });
    window.addEventListener('resize', () => this.fit(), options);
    this.fit();
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.openObserver?.disconnect();
    this.imageObserver?.disconnect();
    this.resizeObserver?.disconnect();
  }
  distance() {
    const [a, b] = [...this.pointers.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  }
  fit() {
    this.media = this.matches('.br-size-chart__media') ? this : this.querySelector('.br-size-chart__media');
    this.image = this.media?.querySelector('img');
    const available = Boolean(this.image);
    const controls = this.dialog?.querySelector('.br-size-chart__zoom-controls');
    if (controls) controls.hidden = !available;
    if (!available || !this.dialog?.open || !this.image.complete || !this.image.naturalWidth) return;
    this.media.tabIndex = 0;
    this.media.setAttribute('aria-label', this.image.alt || 'Size chart');
    const ratio = this.image.naturalWidth / this.image.naturalHeight;
    const maxWidth = Math.min(720, document.documentElement.clientWidth - 32);
    this.baseHeight = Math.min(maxWidth / ratio, window.innerHeight - 96);
    this.baseWidth = this.baseHeight * ratio;
    this.media.style.height = `${this.baseHeight}px`;
    this.media.style.width = `${this.baseWidth}px`;
    this.scale = 1;
    this.zoom(1);
  }
  zoom(value, focalPoint) {
    if (!this.image || !this.baseWidth || !this.media) return;
    const next = Math.min(4, Math.max(1, value));
    const anchor = focalPoint || { x: this.media.clientWidth / 2, y: this.media.clientHeight / 2 };
    const oldWidth = this.baseWidth * this.scale;
    const oldHeight = this.baseHeight * this.scale;
    const u = (this.media.scrollLeft + anchor.x - Math.max(0, (this.media.clientWidth - oldWidth) / 2)) / oldWidth;
    const v = (this.media.scrollTop + anchor.y) / oldHeight;
    const width = this.baseWidth * next;
    this.image.style.width = `${width}px`;
    this.image.style.height = `${this.baseHeight * next}px`;
    this.scale = next;
    this.media.scrollLeft = next === 1 ? 0 : u * width - anchor.x + Math.max(0, (this.media.clientWidth - width) / 2);
    this.media.scrollTop = next === 1 ? 0 : v * this.baseHeight * next - anchor.y;
    this.dialog.querySelector('[data-chart-scale]').textContent = `${Math.round(next * 100)}%`;
    this.dialog.querySelector('[data-chart-zoom="out"]').disabled = next <= 1;
    this.dialog.querySelector('[data-chart-zoom="in"]').disabled = next >= 4;
    this.media.style.cursor = next > 1 ? 'grab' : 'zoom-in';
  }
}
if (!customElements.get('br-size-chart-viewer')) customElements.define('br-size-chart-viewer', BrSizeChartViewer);
